import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import type { AdminIdentifierTypeViewModel } from '../api';

export function DeleteIdentifierTypeConfirmDialog({
  row,
  onCancel,
  onConfirm,
  pending,
  submitError,
}: {
  readonly row: AdminIdentifierTypeViewModel | null;
  readonly onCancel: () => void;
  readonly onConfirm: (row: AdminIdentifierTypeViewModel) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const open = row !== null;
  const submitErrorCode = getErrorCodeForUi(submitError);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('routes.admin.general.identifiers.delete.title')}</DialogTitle>
          <DialogDescription>
            {t('routes.admin.general.identifiers.delete.message', { label: row?.label ?? '' })}
          </DialogDescription>
        </DialogHeader>
        {submitError !== undefined && submitError !== null && (
          <p role="alert" className="text-sm text-red-600">
            {t('routes.admin.general.identifiers.delete.errors.submit', { code: submitErrorCode })}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
            {t('routes.admin.general.identifiers.delete.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            loading={pending}
            onClick={() => {
              if (row !== null) onConfirm(row);
            }}
          >
            {t('routes.admin.general.identifiers.delete.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
